import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import '../styles/AccountApproved.css';

const ROLE_LABELS = { coach: 'coach', trainer: 'trainer', league_admin: 'league admin' };

const AccountApproved = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [continuing, setContinuing] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [roleLabel, setRoleLabel] = useState('');
  const [userId, setUserId] = useState(null);

  // Guard: this screen is a one-time stop. Only an approved-but-not-yet-
  // acknowledged user may see it; everyone else is routed to where they belong.
  useEffect(() => {
    const check = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate('/standouts/login', { replace: true });
          return;
        }

        const { data: approvalData, error } = await supabase
          .rpc('check_user_approval', { p_user_id: user.id });
        if (error || !approvalData || approvalData.length === 0) {
          navigate('/standouts/login', { replace: true });
          return;
        }

        const { approved, role, acknowledged } = approvalData[0];
        if (!approved) {
          navigate('/standouts/pending-approval', { replace: true });
          return;
        }
        if (acknowledged) {
          // Already seen — don't let it show again via direct URL
          navigate('/standouts/submit', { replace: true });
          return;
        }

        // Eligible to see the screen once. Pull the real first name.
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name')
          .eq('id', user.id)
          .single();

        setUserId(user.id);
        setFirstName(profile?.first_name || user.user_metadata?.first_name || '');
        setRoleLabel(ROLE_LABELS[role] || 'account');
        setLoading(false);
      } catch (_) {
        navigate('/standouts/login', { replace: true });
      }
    };
    check();
  }, [navigate]);

  const handleContinue = async () => {
    if (continuing || !userId) return;
    setContinuing(true);
    // Mark the screen as seen so it never shows again, then proceed.
    await supabase.rpc('acknowledge_approval', { p_user_id: userId });
    navigate('/standouts/submit', { replace: true });
  };

  if (loading) {
    return (
      <div className="aa-screen">
        <div className="aa-container" />
      </div>
    );
  }

  return (
    <div className="aa-screen">
      <div className="aa-container">

        <div className="aa-logo-section">
          <img src="/assets/images/logo.png" alt="LegacyStat" className="aa-logo" />
          <div className="aa-logo-divider">
            <span className="aa-divider-line" />
            <span className="aa-standouts-label">STANDOUTS</span>
            <span className="aa-divider-line" />
          </div>
        </div>

        <div className="aa-check-circle">
          <img src="/assets/images/check-aa.png" alt="Approved" className="aa-check-img" />
        </div>

        <h1 className="aa-title">ACCOUNT APPROVED!</h1>
        <p className="aa-subtitle">YOU'RE ALL SET TO MAKE AN IMPACT</p>

        <div className="aa-card">
          <h2 className="aa-greeting">Hi{firstName ? ` ${firstName}` : ''},</h2>

          <p className="aa-message">
            Great news! Your {roleLabel} account has been approved. You can now join an
            organization and start recognizing athletes through{' '}
            <span className="aa-highlight">Standouts.</span>
          </p>

          <div className="aa-steps-box">
            <p className="aa-steps-label">NEXT STEPS</p>
            <div className="aa-steps">
              <div className="aa-step">
                <div className="aa-step-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <span className="aa-step-num">1</span>
                <span className="aa-step-text">Log in to<br />your account</span>
              </div>

              <span className="aa-step-arrow">›</span>

              <div className="aa-step">
                <div className="aa-step-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <span className="aa-step-num">2</span>
                <span className="aa-step-text">Join or create<br />an organization</span>
              </div>

              <span className="aa-step-arrow">›</span>

              <div className="aa-step">
                <div className="aa-step-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                </div>
                <span className="aa-step-num">3</span>
                <span className="aa-step-text">Wait for admin<br />to approve</span>
              </div>

              <span className="aa-step-arrow">›</span>

              <div className="aa-step">
                <div className="aa-step-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </div>
                <span className="aa-step-num">4</span>
                <span className="aa-step-text">Start submitting<br />Standouts!</span>
              </div>
            </div>
          </div>

          <button className="aa-btn" onClick={handleContinue} disabled={continuing}>
            {continuing ? 'LOADING…' : 'CONTINUE →'}
          </button>

          <div className="aa-support">
            <div className="aa-support-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <div className="aa-support-text">
              <span className="aa-support-label">Questions? We're here to help.</span>
              <a href="mailto:support@legacystat.com" className="aa-support-email">support@legacystat.com</a>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AccountApproved;
